/**
 * getPrimaryColour
 * ----------------
 * Returns the app's current primary color from CSS variables, with optional
 * format and opacity control.
 *
 * @param {Object} [options]
 * @param {"hex"|"rgb"} [options.format="hex"] - Output format.
 * @param {number} [options.opacity=1] - Opacity (0–1) when format === "rgb".
 *
 * Example usage:
 *   getPrimaryColour()                          → "#852170"
 *   getPrimaryColour({ format: "rgb" })          → "rgb(133,33,112)"
 *   getPrimaryColour({ format: "rgb", opacity: .3 }) → "rgba(133,33,112,0.3)"
 */
export function getPrimaryColour({ format = "hex", opacity = 1 } = {}) {
  if (typeof window === "undefined") return "#51aed1"; // SSR safety

  const rootStyle = getComputedStyle(document.documentElement);
  const colorVar =
    rootStyle.getPropertyValue("--color-primary") ||
    rootStyle.getPropertyValue("--tw-color-primary") ||
    "#51aed1";

  const color = colorVar.trim() || "#51aed1";

  // If RGB format requested, convert hex → RGB(A)
  if (format === "rgb") {
    const hex = color.startsWith("#") ? color.slice(1) : color;
    const bigint = parseInt(
      hex.length === 3
        ? hex
            .split("")
            .map((c) => c + c)
            .join("")
        : hex,
      16
    );

    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    if (opacity < 1) return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    return `rgb(${r}, ${g}, ${b})`;
  }

  // Default: return hex color string
  return color;
}
